import { queriesRequirementDefinitions } from "@query";
import {
  WSActions,
  WSCard,
  WSForm,
  WSInputTextarea,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  RequirementDefinition,
  RequirementDefinitionTransitionStrategy,
  SharedFileRequestConfiguration,
  UpdateRequirementDefinitionRequest
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import * as Yup from "yup";

import { WSQueries } from "../../../../query/WSQuery";
import { StepComponentProps } from "../../../../shared/components/ModalFlow/types";
import { getStartOfDay } from "../../../../utils/dates";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { RequirementInstructionsView } from "../../../Requirements/components/RequirementInstructionsView/RequirementInstructionsView";
import { useCreateRequirementDefinitionSettings } from "../../hooks/useCreateRequirementDefinitionSettings";
import { useRequirementDefinitionSettings } from "../../hooks/useRequirementDefinitionSettings";
import { InsuranceInstructionsView } from "../../routes/RouteCreateRequirementByTemplate/components/InsuranceInstructionsView";
import { RequirementDefinitionTemplate } from "../../routes/RouteCreateRequirementByTemplate/types";
import { getRequirementDefinitionTemplate } from "../../utils/getRequirementDefinitionTemplate";
import { getVersionNoteValidator } from "../../utils/validators";
import {
  convertFormApprovalDataToReviewStrategy,
  convertFormDataToExpirationStrategy
} from "../CreateRequirementDefinitionForm/utils";
import { RequirementInformationCard } from "../RequirementDefinitionInfoCard";

import {
  FlowCreateNewVersionSettingsData,
  FlowCreateNewVersionState
} from "./types";

const EXPIRATION_FIELDS: (keyof FlowCreateNewVersionSettingsData)[] = [
  "expirationStrategy",
  "expirationDays",
  "manualRenewalAllowed"
];

const APPROVAL_FIELDS: (keyof FlowCreateNewVersionSettingsData)[] = [
  "reviewStrategy",
  "isApprovalRequired"
];

const NON_SETTINGS_FIELDS: (keyof FlowCreateNewVersionSettingsData)[] = [
  ...EXPIRATION_FIELDS,
  ...APPROVAL_FIELDS
];

const isFieldsChanged = (
  data: Partial<FlowCreateNewVersionSettingsData>,
  fields: typeof NON_SETTINGS_FIELDS
) => {
  return fields.some(field => field in data);
};

const isSomethingElseChanged = (
  data: Partial<FlowCreateNewVersionSettingsData>,
  fields: typeof NON_SETTINGS_FIELDS
) => {
  return Object.keys(data).some(dataKey => !fields.includes(dataKey as any));
};

export const StepReviewAndCreate: React.FC<
  StepComponentProps<FlowCreateNewVersionState>
> = ({ onBack, onSuccess, goToSideStep, state, setState }) => {
  const queryRequirementDefinition =
    queriesRequirementDefinitions.getRequirementDefinition.useQuery({
      requirementDefinitionId: state.requirementDefinitionId
    });

  const { dataSourceError, submit, isLoading } =
    useCreateRequirementDefinitionSettings();

  const [updateRequirementDefinition, updateRequirementDefinitionMeta] =
    queriesRequirementDefinitions.updateRequirementDefinition.useMutation();

  const handleSubmit = async (
    state: FlowCreateNewVersionState,
    template: RequirementDefinitionTemplate,
    requirementDefinition: RequirementDefinition
  ) => {
    // v1 carried scope/strategy/expiry flat on the update; v3 nests them under
    // `transition`.
    let updateRequest: UpdateRequirementDefinitionRequest = {
      versionNote: state.versionNote || "",
      transition: {
        scope: state.transitionData?.scope!,
        strategy: state.transitionData?.transitionMethod!
      }
    };

    if (
      state.transitionData?.transitionMethod ===
      RequirementDefinitionTransitionStrategy.RENEWAL
    ) {
      updateRequest.transition!.expiresAt = getStartOfDay(
        state.transitionData?.renewalDeadlineDays
      );
    }

    if (state.settingsData?.name !== requirementDefinition.name) {
      updateRequest.name = state.settingsData?.name;
    }

    if (state.settingsData?.description !== requirementDefinition.description) {
      updateRequest.description = state.settingsData?.description;
    }

    if (
      state?.changedData &&
      isSomethingElseChanged(state?.changedData!, NON_SETTINGS_FIELDS)
    ) {
      const settingsResponse = await submit({
        formData: {
          type: requirementDefinition.type!,
          ...state.settingsData!
        },
        template
      });

      updateRequest.dataSourceId = settingsResponse.dataSourceId;
    }

    if (isFieldsChanged(state?.changedData!, APPROVAL_FIELDS)) {
      updateRequest.reviewPolicy = convertFormApprovalDataToReviewStrategy({
        contextType: (querySettingsItem.data as SharedFileRequestConfiguration)
          ?.contextType,
        isApprovalRequired: !!state?.settingsData?.isApprovalRequired,
        reviewStrategy: state.settingsData?.reviewStrategy!,
        type: queryRequirementDefinition.data?.type!
      });
    }

    if (isFieldsChanged(state?.changedData!, EXPIRATION_FIELDS)) {
      updateRequest = {
        ...updateRequest,
        ...convertFormDataToExpirationStrategy(state.settingsData!)
      };
    }
    await updateRequirementDefinition(
      {
        requirementDefinitionId: state.requirementDefinitionId,
        updateRequirementDefinitionRequest: updateRequest
      },
      {
        onSuccess() {
          goToSideStep("success");
        }
      }
    );
  };

  const querySettingsItem = useRequirementDefinitionSettings({
    dataSourceId: queryRequirementDefinition.data?.dataSourceId,
    requirementType: queryRequirementDefinition.data?.type
  });

  return (
    <WSQueries queries={{ queryRequirementDefinition, querySettingsItem }}>
      {({ queryRequirementDefinitionData, querySettingsItemData }) => {
        const template = getRequirementDefinitionTemplate(
          queryRequirementDefinitionData,
          querySettingsItemData
        );

        const acceptWingspanMasterPolicy =
          state.settingsData &&
          "acceptWingspanMasterPolicy" in state.settingsData
            ? state.settingsData.acceptWingspanMasterPolicy
            : undefined;

        return (
          <WSList gap="2XL">
            <WSText.Heading4>Review & create</WSText.Heading4>
            <WSText.ParagraphSm color="gray500">
              This step will allow you to review all settings before creating
              the new version. Coming soon.
            </WSText.ParagraphSm>

            <RequirementInformationCard
              description={state.settingsData?.description!}
              name={state.settingsData?.name!}
              template={template}
              version={(queryRequirementDefinitionData.version ?? 1) + 1}
            />

            {template.instructions ||
            state.settingsData?.instructions ||
            template.isPolicy ? (
              <WSCard
                header={{
                  label: {
                    helperText:
                      "The following instructions will be shown to the contractors.",
                    text: "Instructions to contractors"
                  }
                }}
              >
                <WSList gap="2XL">
                  {template.isPolicy ? (
                    <InsuranceInstructionsView
                      allowMasterPolicy={acceptWingspanMasterPolicy}
                      insuranceName={template.name}
                      policyTypes={template.policyType}
                    />
                  ) : null}
                  {!template.isPolicy &&
                  (template.instructions ||
                    state.settingsData?.instructions) ? (
                    <WSCard>
                      <RequirementInstructionsView
                        instructions={
                          template.instructions ||
                          state.settingsData?.instructions!
                        }
                      />
                    </WSCard>
                  ) : null}
                </WSList>
              </WSCard>
            ) : null}

            <WSForm
              defaultValues={{
                versionNote: ""
              }}
              validationSchema={Yup.object({
                versionNote: getVersionNoteValidator(
                  (queryRequirementDefinitionData.version ?? 1) + 1
                )
              })}
              onSubmit={async formData => {
                const newState: FlowCreateNewVersionState = {
                  ...state,
                  ...formData
                };

                setState(formData);
                await handleSubmit(
                  newState,
                  template,
                  queryRequirementDefinitionData
                );
              }}
            >
              <WSList gap="2XL">
                <WSCard
                  header={{
                    label: {
                      helperText:
                        "Add context about why this version was created or what changed. This note is only visible to your team.",
                      text: "Internal note"
                    }
                  }}
                >
                  <WSForm.Field
                    component={WSInputTextarea}
                    componentProps={{
                      pill: {
                        icon: "lock",
                        text: "Private",
                        theme: "neutral"
                      }
                    }}
                    label="Internal note"
                    name="versionNote"
                    placeholder="Add a note..."
                  />
                </WSCard>

                <ErrorAlert
                  context="Other"
                  error={
                    dataSourceError || updateRequirementDefinitionMeta.error
                  }
                />

                <WSActions
                  alignment="fill"
                  buttons={[
                    {
                      kind: "Secondary",
                      label: "Back",
                      onClick: onBack,
                      type: "button"
                    },
                    {
                      kind: "Primary",
                      label: "Create version",
                      type: "submit"
                    }
                  ]}
                />
              </WSList>
            </WSForm>
          </WSList>
        );
      }}
    </WSQueries>
  );
};
